import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../utils/supabase';
import { Category } from '../types';
import { useNavigate } from 'react-router-dom';
import { buildCategoryTree } from '../utils/categoryHelpers';

const AdminCategories: React.FC = () => {
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [categoryTree, setCategoryTree] = useState<Category[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '', parent_id: null as number | null });
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('habib_categories')
      .select('*')
      .order('name', { ascending: true });
    if (data) {
      setAllCategories(data);
      setCategoryTree(buildCategoryTree(data));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      parent_id: formData.parent_id || null,
    };

    if (editingCategory) {
      await supabase
        .from('habib_categories')
        .update(payload)
        .eq('id', editingCategory.id);
    } else {
      await supabase
        .from('habib_categories')
        .insert([payload]);
    }

    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '', parent_id: null });
    fetchCategories();
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id ?? null,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    const subs = allCategories.filter(c => c.parent_id === id);
    const message = subs.length > 0
      ? `This category has ${subs.length} sub-categor${subs.length === 1 ? 'y' : 'ies'}. Deleting it will also delete all sub-categories. Continue?`
      : 'Are you sure you want to delete this category?';

    if (window.confirm(message)) {
      await supabase.from('habib_categories').delete().eq('id', id);
      fetchCategories();
    }
  };

  const handleAddNew = (parentId: number | null = null) => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', parent_id: parentId });
    setShowModal(true);
  };

  const mainCategories = allCategories.filter(c => !c.parent_id);

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-xl mr-3 flex items-center justify-center shadow-lg overflow-hidden" style={{backgroundColor: '#05472A'}}>
                <img
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold" style={{color: '#05472A'}}>Category Product Management</h1>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm" style={{color: '#05472A', borderColor: '#05472A40'}}>
                {t('back')}
              </button>
              <span className="text-sm" style={{color: '#05472A'}}>{t('hi')}, {user?.username}</span>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                {t('signOut')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-soft border p-8" style={{borderColor: '#05472A20'}}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{color: '#05472A'}}>Categories Product</h2>
            <button
              onClick={() => handleAddNew()}
              className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium"
              style={{backgroundColor: '#05472A'}}
            >
              + Add Category
            </button>
          </div>

          <div className="space-y-4">
            {categoryTree.map((main) => (
              <div
                key={main.id}
                className="border rounded-xl p-5 hover:shadow-soft transition-all duration-300"
                style={{borderColor: '#05472A20'}}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-bold text-lg" style={{color: '#05472A'}}>{main.name}</h3>
                    <p className="text-sm" style={{color: '#05472A99'}}>{main.description || 'No description'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAddNew(main.id)}
                      className="border px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-50 transition-all"
                      style={{color: '#05472A', borderColor: '#05472A40'}}
                    >
                      + Sub
                    </button>
                    <button
                      onClick={() => handleEdit(main)}
                      className="border px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-50 transition-all"
                      style={{color: '#05472A', borderColor: '#05472A40'}}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(main.id)}
                      className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-600 transition-all"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Sub-categories */}
                {main.subcategories && main.subcategories.length > 0 && (
                  <div className="mt-3 ml-4 space-y-2 border-l-2 pl-4" style={{borderColor: '#05472A20'}}>
                    {main.subcategories.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-green-50 transition-all">
                        <div>
                          <p className="font-semibold text-sm" style={{color: '#05472A'}}>{sub.name}</p>
                          {sub.description && (
                            <p className="text-xs" style={{color: '#05472A80'}}>{sub.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(sub)}
                            className="border px-3 py-1 rounded-lg text-xs font-semibold hover:bg-green-50 transition-all"
                            style={{color: '#05472A', borderColor: '#05472A40'}}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(sub.id)}
                            className="bg-red-500 text-white px-3 py-1 rounded-lg text-xs font-semibold hover:bg-red-600 transition-all"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <h3 className="text-2xl font-bold mb-6" style={{color: '#05472A'}}>
              {editingCategory ? 'Edit Category' : formData.parent_id ? 'Add Sub-Category' : 'Add New Category'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>
                  Parent Category (optional)
                </label>
                <select
                  value={formData.parent_id ?? ''}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value ? Number(e.target.value) : null })}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                  style={{borderColor: '#05472A20', '--tw-ring-color': '#05472A'} as any}
                >
                  <option value="">None (Main Category)</option>
                  {mainCategories
                    .filter(c => !editingCategory || c.id !== editingCategory.id)
                    .map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))
                  }
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                  style={{borderColor: '#05472A20', '--tw-ring-color': '#05472A'} as any}
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 h-24"
                  style={{borderColor: '#05472A20', '--tw-ring-color': '#05472A'} as any}
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({ name: '', description: '', parent_id: null });
                  }}
                  className="flex-1 border px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                  style={{borderColor: '#05472A40', color: '#05472A'}}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-soft hover:shadow-medium"
                  style={{backgroundColor: '#05472A'}}
                >
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
